import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ScopeProvider } from '../contexts/ScopeContext';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import Layout from './Layout';
import LoadingSpinner from './LoadingSpinner';
import ErrorBoundary from './ErrorBoundary';
import { useAuth, useProjects, useLogout } from '../hooks/useApi';
import { Project } from '../types';
import { apiService } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-client';

const SessionsPage = lazy(() => import('../pages/SessionsPage'));
const SessionDetailPage = lazy(() => import('../pages/SessionDetailPage'));
const AccountSettingsPage = lazy(() => import('../pages/AccountSettingsPage'));
const IssuesPage = lazy(() => import('../pages/IssuesPage'));
const IssueDetailPage = lazy(() => import('../pages/IssueDetailPage'));
const ReviewQueuePage = lazy(() => import('../pages/ReviewQueuePage'));
const VariantDetailPage = lazy(() => import('../pages/VariantDetailPage'));
const ExperimentsPage = lazy(() => import('../pages/ExperimentsPage'));
const ExperimentDetailPage = lazy(() => import('../pages/ExperimentDetailPage'));
const ResultsDashboardPage = lazy(() => import('../pages/ResultsDashboardPage'));
const AutoRolloutPage = lazy(() => import('../pages/AutoRolloutPage'));
const FeedbackLoopPage = lazy(() => import('../pages/FeedbackLoopPage'));
const ReportingPage = lazy(() => import('../pages/ReportingPage'));
const JourneysPage = lazy(() => import('../pages/JourneysPage'));
const JourneyDetailPage = lazy(() => import('../pages/JourneyDetailPage'));

export default function AppContent() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: authState, isLoading: authLoading } = useAuth();
  const isAuthenticated = authState?.isAuthenticated || false;

  const projectIdsSignature = useMemo(() => {
    const ids = authState?.session?.user.projectIds;
    return ids && ids.length > 0 ? ids.slice().sort().join('|') : '::empty';
  }, [authState?.session?.user.projectIds]);

  const { data: projectsResponse, isLoading: projectsLoading } = useProjects({
    enabled: isAuthenticated,
  });
  const logoutMutation = useLogout();

  const projects = projectsResponse?.data || [];
  const showLoading = authLoading || (isAuthenticated && projectsLoading);

  // Default-select the first project ONLY on initial arrival.
  // Without the ref, this effect fires every time `selectedProject` flips to
  // null — including when the user explicitly picks "All projects" — and
  // immediately re-selects projects[0], clobbering the user's choice.
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || projects.length === 0) return;
    if (hasAutoSelectedRef.current) return;
    // Skip auto-select if the URL is asking for cross-project scope.
    if (location.search.includes('scope=all')) {
      hasAutoSelectedRef.current = true;
      return;
    }
    if (!selectedProject) {
      setSelectedProject(projects[0]);
    }
    hasAutoSelectedRef.current = true;
  }, [isAuthenticated, projects, selectedProject, location.search]);

  const handleAuth0Login = useCallback((options?: { screenHint?: 'signup' }) => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`.trim();
    apiService.beginAuth0Login({
      returnTo: returnTo.length > 0 ? returnTo : undefined,
      screenHint: options?.screenHint,
    });
  }, []);

  // Refetch projects when the authenticated user's projectIds list changes.
  useEffect(() => {
    if (!isAuthenticated) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.projects.lists() });
  }, [projectIdsSignature, isAuthenticated, queryClient]);

  // Sync `selectedProject` with the URL where it is encoded
  // (`/sessions/:projectId` and the legacy `/project/:projectId`).
  useEffect(() => {
    if (!isAuthenticated || projects.length === 0) return;
    const segments = location.pathname.split('/');
    let projectIdParam: string | undefined;
    if (segments[1] === 'project' && segments[2]) {
      projectIdParam = decodeURIComponent(segments[2]);
    } else if (segments[1] === 'sessions' && segments[2] && !segments[2].startsWith('s_')) {
      // /sessions/:projectId — distinguish from /sessions/:sessionId.
      // Session IDs follow `s_…` convention; the project route is everything else.
      const candidate = decodeURIComponent(segments[2]);
      const match = projects.find((p) => p.projectId === candidate || p._id === candidate);
      if (match) projectIdParam = candidate;
    }
    if (projectIdParam) {
      const project =
        projects.find((p) => p.projectId === projectIdParam) ||
        projects.find((p) => p._id === projectIdParam);
      if (project && (!selectedProject || selectedProject._id !== project._id)) {
        setSelectedProject(project);
      }
    }
  }, [location.pathname, projects, isAuthenticated, selectedProject]);

  // Global keyboard shortcuts. Bound only while authenticated so the landing
  // page doesn't intercept stray keystrokes. Per-page shortcuts (j/k on the
  // queue, space/arrows on replay) wire themselves locally.
  const navigate = useNavigate();
  const globalShortcuts = useMemo(
    () => [
      {
        keys: 'g d',
        description: 'Go to sessions',
        handler: () => navigate('/sessions'),
      },
      {
        keys: 'g i',
        description: 'Go to issues',
        handler: () => navigate('/issues'),
      },
      {
        keys: 'g r',
        description: 'Go to review queue',
        handler: () => navigate('/review'),
      },
      {
        keys: 'g e',
        description: 'Go to experiments',
        handler: () => navigate('/experiments'),
      },
      {
        keys: 'g s',
        description: 'Go to account settings',
        handler: () => navigate('/account'),
      },
    ],
    [navigate],
  );
  useKeyboardShortcuts(globalShortcuts, isAuthenticated);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync({ returnTo: window.location.origin });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setSelectedProject(null);
    }
  };

  const handleProjectChange = (project: Project | null) => {
    setSelectedProject(project);
  };

  const handleProjectDeleted = () => {
    setSelectedProject(null);
  };

  // Unauthenticated users are bounced straight to the Auth0 hosted login —
  // marketing lives on the separate `sessions.chxse.dev` site, the app
  // subdomain just authenticates. Effect intentionally has no `handleAuth0Login`
  // dep; it only needs to fire once when auth state resolves to unauth.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      handleAuth0Login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  if (showLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ScopeProvider
      selectedProject={selectedProject}
      accessibleProjects={projects}
      setSelectedProject={handleProjectChange}
    >
    <Layout
      projects={projects}
      selectedProject={selectedProject}
      onProjectChange={handleProjectChange}
      onLogout={handleLogout}
      onProjectDeleted={handleProjectDeleted}
    >
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-24">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          <Routes>
            {/* Legacy paths fold into the unified /sessions surface. */}
            <Route path="/all-sessions" element={<Navigate to="/sessions" replace />} />
            <Route
              path="/dashboard"
              element={
                selectedProject ? (
                  <Navigate
                    to={`/sessions/${encodeURIComponent(selectedProject.projectId)}`}
                    replace
                  />
                ) : (
                  <Navigate to="/sessions" replace />
                )
              }
            />
            <Route
              path="/sessions"
              element={
                <SessionsRootRoute
                  projects={projects}
                  selectedProject={selectedProject}
                  onProjectChange={handleProjectChange}
                />
              }
            />
            <Route
              path="/sessions/:projectId"
              element={
                <SessionsScopedRoute
                  projects={projects}
                  selectedProject={selectedProject}
                  onProjectChange={handleProjectChange}
                />
              }
            />
            <Route
              path="/sessions/:projectId/:sessionId"
              element={
                <SessionDetailPage
                  projectId={selectedProject?._id || ''}
                  onProjectChange={handleProjectChange}
                  projects={projects}
                />
              }
            />
            {/* Legacy singular: /session/:sessionId — redirect to the
                projectId-scoped path. Reads `?projectId=` first, then falls
                back to the active selectedProject. */}
            <Route
              path="/session/:sessionId"
              element={
                <LegacySessionRedirect selectedProject={selectedProject} />
              }
            />
            <Route path="/issues" element={<IssuesPage />} />
            <Route path="/issues/:issueId" element={<IssueDetailPage />} />
            <Route path="/review" element={<ReviewQueuePage />} />
            <Route path="/review/:variantId" element={<VariantDetailPage />} />
            <Route path="/experiments" element={<ExperimentsPage />} />
            <Route path="/experiments/:experimentId" element={<ExperimentDetailPage />} />
            <Route path="/journeys" element={<JourneysPage />} />
            <Route path="/journeys/:journeyId" element={<JourneyDetailPage />} />
            <Route path="/results" element={<ResultsDashboardPage />} />
            <Route path="/auto-rollout" element={<AutoRolloutPage />} />
            <Route path="/feedback" element={<FeedbackLoopPage />} />
            <Route path="/reports" element={<ReportingPage />} />
            <Route path="/settings" element={<AccountSettingsPage />} />
            <Route path="/" element={<Navigate to="/sessions" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Layout>
    </ScopeProvider>
  );
}

interface SessionsScopedRouteProps {
  projects: Project[];
  selectedProject: Project | null;
  onProjectChange: (project: Project | null) => void;
}

// `/sessions/:projectId` — locks scope to the routed project. If the route's
// projectId doesn't match any project we know about, fall through to the
// "all projects" view so the user isn't stranded on a 404.
function SessionsScopedRoute({ projects, selectedProject, onProjectChange }: SessionsScopedRouteProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const matched = projects.find(
    (p) => p.projectId === projectId || p._id === projectId,
  );
  if (!matched) {
    return <Navigate to="/sessions" replace />;
  }
  return (
    <SessionsPage
      scope="this-project"
      projects={projects}
      selectedProject={selectedProject ?? matched}
      onProjectChange={onProjectChange}
    />
  );
}

// `/session/:sessionId` — legacy singular form. The new canonical shape is
// `/sessions/:projectId/:sessionId`. Read the projectId from the search
// (`?projectId=…`); if it's missing, fall back to the selected project; if
// even that's missing, bounce to `/sessions`.
function LegacySessionRedirect({ selectedProject }: { selectedProject: Project | null }) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  if (!sessionId) {
    return <Navigate to="/sessions" replace />;
  }
  const fromQuery = searchParams.get('projectId') || '';
  const projectId = fromQuery || selectedProject?.projectId || selectedProject?._id || '';
  if (!projectId) {
    return <Navigate to="/sessions" replace />;
  }
  return (
    <Navigate
      to={`/sessions/${encodeURIComponent(projectId)}/${encodeURIComponent(sessionId)}`}
      replace
    />
  );
}

// `/sessions` (no project) — defaults to the user's active project's view
// instead of the cross-project firehose. ?scope=all opts into the wide view.
function SessionsRootRoute({ projects, selectedProject, onProjectChange }: SessionsScopedRouteProps) {
  const location = useLocation();
  const wantAllProjects = new URLSearchParams(location.search).get('scope') === 'all';
  if (selectedProject && !wantAllProjects) {
    return (
      <Navigate
        to={`/sessions/${encodeURIComponent(selectedProject.projectId)}`}
        replace
      />
    );
  }
  return (
    <SessionsPage
      scope="all-projects"
      projects={projects}
      selectedProject={selectedProject}
      onProjectChange={onProjectChange}
    />
  );
}
